import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Grid } from '@mui/material';
import { getStatusColor } from './themeUtils';
import { Info as AssignedIcon, MoreHoriz as InProgressIcon, CheckCircle as CompletedIcon, Cancel as RejectedIcon, ForwardToInbox as ForwardIcon } from '@mui/icons-material';

/**
 * A reusable card to display counts by status.
 * Expects a `stats` object whose keys are statuses and values are counts.
 * Expects a `statuses` array of statuses.
 */
const StatusStatsCard = ({ stats = {}, statuses = [] }) => {
  const { t } = useTranslation(['dashboard', 'tasks']);

  // Calculate total for percentage calculation
  const total = Object.values(stats).reduce((acc, val) => acc + val, 0);

  const getStatusIcon = (status, color) => {
    const props = { fontSize: 'small', color };
    switch (status) {
      case 'ASSIGNED':
        return <AssignedIcon {...props} />;
      case 'IN_PROGRESS':
        return <InProgressIcon {...props} />;
      case 'COMPLETED':
        return <CompletedIcon {...props} />;
      case 'REJECTED':
        return <RejectedIcon {...props} />;
      case 'FORWARDED':
        return <ForwardIcon {...props} />;
      default:
        return <AssignedIcon {...props} />;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Grid container spacing={1} alignItems="stretch" justifyContent="center" sx={{ flexWrap: 'nowrap' }}>
        {statuses.map((status) => {
          const percentage = total > 0 ? ((stats[status] || 0) / total) * 100 : 0;
          const colorKey = getStatusColor(status);
          return (
            <Grid item key={status} sx={{ flex: '1 1 auto', minWidth: 'fit-content' }}>
              <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, backgroundColor: 'background.paper', boxShadow: 1, minHeight: 130, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box sx={{ mb: 0.5 }}>{getStatusIcon(status, colorKey)}</Box>
                <Typography variant="h6" component="div" color={colorKey} sx={{ mb: 0.5, fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                  {stats[status] || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                  {t(`tasks:status_${status.toLowerCase()}`, status)}
                </Typography>
                <Box sx={{ width: '100%', height: 6, borderRadius: 3, backgroundColor: 'grey.300', mt: 0.5 }}>
                  <Box sx={{ width: `${percentage}%`, height: '100%', borderRadius: 3, bgcolor: (theme) => theme.palette[colorKey]?.main || theme.palette.primary.main }} />
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default StatusStatsCard;
